/**
 * Invoices API client
 */

import { apiClient } from './client';
import type { 
  Invoice, 
  InvoiceCreate, 
  InvoiceUpdate, 
  InvoiceFilter,
  InvoicePaginationParams,
  InvoiceListResponse,
  InvoiceSummary
} from '../../types/invoice';

export const invoicesApi = {
  /**
   * List invoices with filters and pagination
   */
  async list(
    filters?: InvoiceFilter,
    pagination?: InvoicePaginationParams
  ): Promise<InvoiceListResponse> {
    const params: Record<string, any> = {};
    
    // Filters
    if (filters?.projectId) params.project_id = filters.projectId;
    if (filters?.status) params.status = filters.status;
    if (filters?.startDate) params.start_date = filters.startDate;
    if (filters?.endDate) params.end_date = filters.endDate;
    if (filters?.minAmount) params.min_amount = filters.minAmount;
    if (filters?.maxAmount) params.max_amount = filters.maxAmount;
    if (filters?.search) params.search = filters.search;
    if (filters?.issuerName) params.issuer_name = filters.issuerName;
    if (filters?.userId) params.user_id = filters.userId;
    
    // Pagination
    if (pagination?.page) params.page = pagination.page;
    if (pagination?.pageSize) params.page_size = pagination.pageSize;
    if (pagination?.sortBy) params.sort_by = pagination.sortBy;
    if (pagination?.sortOrder) params.sort_order = pagination.sortOrder;
    
    const response = await apiClient.get<InvoiceListResponse>('/api/v1/invoices', { params });
    return response.data;
  },

  /**
   * Get invoice by ID
   */
  async getById(invoiceId: string): Promise<Invoice> {
    const response = await apiClient.get<Invoice>(`/api/v1/invoices/${invoiceId}`);
    return response.data;
  },

  /**
   * Create a new invoice
   */
  async create(data: InvoiceCreate): Promise<Invoice> {
    const response = await apiClient.post<Invoice>('/api/v1/invoices', data);
    return response.data;
  },

  /**
   * Update invoice
   */
  async update(invoiceId: string, data: InvoiceUpdate): Promise<Invoice> {
    const response = await apiClient.put<Invoice>(`/api/v1/invoices/${invoiceId}`, data);
    return response.data;
  },

  /**
   * Delete invoice
   */
  async delete(invoiceId: string): Promise<void> {
    await apiClient.delete(`/api/v1/invoices/${invoiceId}`);
  },

  /**
   * Get project invoice summary
   */
  async getProjectSummary(projectId: string): Promise<InvoiceSummary> {
    const response = await apiClient.get<InvoiceSummary>(
      `/api/v1/projects/${projectId}/invoices/summary`
    );
    return response.data;
  },
};
